import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import PlusMenu from 'llm/chat-library/PlusMenu';
import {
  getAgentDescriptionForChatInput,
  getAgentTypeIcon,
  getAgentTypeText,
  getAvailableAgentTypes,
  getDefaultAgentType,
  doesAgentTypeSupportRealtimeVoice,
  isAssistantUnifiedToolbarAgentType,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {
  RealtimeVoiceSessionControls,
  RealtimeVoiceSessionState,
} from 'llm/realtime-voice/useRealtimeVoiceSession';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import Spinner from 'ui-library/Spinner';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';
import {listToReadable} from 'util/NameUtil';

/** Shared layout for unified picker option tooltips (strong headline + italic byline). */
function unifiedPickerOptionTooltip(headline: string, byline: React.ReactNode): JSX.Element {
  return (
    <>
      <strong>{headline}</strong>
      {!headline.endsWith('.') ? '.' : null} <em>{byline}</em>
    </>
  );
}

function assistantUnifiedAgentOptionTooltip(replacedAgentTypes: Array<AgentType>): JSX.Element {
  const headline =
    getAgentDescriptionForChatInput(AgentType.ASSISTANT) ?? getAgentTypeText(AgentType.ASSISTANT);
  const assistantLabel = getAgentTypeText(AgentType.ASSISTANT);
  const listPhrase = listToReadable(replacedAgentTypes.map((t) => getAgentTypeText(t)));
  const byline =
    listPhrase.length > 0 ? (
      <>
        {assistantLabel} is replacing {listPhrase}. Give it a try and let us know if it needs improvements.
      </>
    ) : (
      <>Give it a try and let us know if it needs improvements.</>
    );
  return unifiedPickerOptionTooltip(headline, byline);
}

/** Tooltip body for legacy agent rows in the unified picker (Option tooltipTitle supports JSX). */
function legacyUnifiedAgentOptionTooltip(agentType: AgentType): JSX.Element {
  const label = getAgentTypeText(agentType);
  const headline = getAgentDescriptionForChatInput(agentType) ?? label;
  return unifiedPickerOptionTooltip(
    headline,
    <>
      {label} is being replaced by Assistant, which has all of the same capabilities. Give Assistant a try and
      let us know if it needs improvements.
    </>
  );
}

export function UnifiedAssistantAgentToolbarDropdown({
  disabled,
  selectedAgentType,
  unifiedAgentsInOrder,
  setAgentType,
}: {
  disabled: boolean;
  selectedAgentType: AgentType;
  unifiedAgentsInOrder: Array<AgentType>;
  setAgentType: (agentType: AgentType | null) => void;
}): JSX.Element {
  const hasAssistant = unifiedAgentsInOrder.includes(AgentType.ASSISTANT);
  const withoutAssistant = unifiedAgentsInOrder.filter((a) => a !== AgentType.ASSISTANT);
  const isActive = true;

  const menu = (
    <Menu
      scrollable={true}
      height={MenuHeight.SMALL}
      width={MenuWidth.LARGE}
      value={selectedAgentType}
      useSelectedIconIfSingleSelect={true}
    >
      {hasAssistant && (
        <Section>
          <Option
            value={AgentType.ASSISTANT}
            onSelect={() => {
              setAgentType(AgentType.ASSISTANT);
            }}
            tooltipTitle={assistantUnifiedAgentOptionTooltip(withoutAssistant)}
            tooltipPlacement={FloatingPlacement.RIGHT}
          >
            <div className={css(styles.agentMenuOptionWithTag)}>
              <span className={css(styles.agentMenuOptionLabel)}>
                {getAgentTypeText(AgentType.ASSISTANT)}
              </span>
              <div className={css(styles.agentMenuOptionTagSlot)}>
                <Tag size={TagSize.SMALL} color={TagColor.BETA} text="BETA" />
              </div>
            </div>
          </Option>
        </Section>
      )}
      {withoutAssistant.length > 0 && (
        <Section>
          {withoutAssistant.map((agentOption) => (
            <Option
              key={agentOption}
              value={agentOption}
              onSelect={() => {
                setAgentType(agentOption);
              }}
              tooltipTitle={legacyUnifiedAgentOptionTooltip(agentOption)}
              tooltipPlacement={FloatingPlacement.RIGHT}
            >
              <div className={css(styles.agentMenuOptionWithTag)}>
                <span className={css(styles.agentMenuOptionLabel)}>{getAgentTypeText(agentOption)}</span>
                <div className={css(styles.agentMenuOptionTagSlot)}>
                  <Tag size={TagSize.SMALL} color={TagColor.WARN} text="LEGACY" />
                </div>
              </div>
            </Option>
          ))}
        </Section>
      )}
    </Menu>
  );

  return (
    <Dropdown
      menu={menu}
      closeOnInsideClick={true}
      closeOnOutsideClick={true}
      allowMenuToCloseDropdown={false}
    >
      <Button
        aria-pressed={isActive}
        className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
        size={ButtonSize.MINI}
        buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
        icon={getAgentTypeIcon(selectedAgentType) ?? undefined}
        textClassName={css(styles.agentTypeText)}
        text={getAgentTypeText(selectedAgentType)}
        disabled={disabled}
        isDropdown={true}
        dataTestAttrs={{component: 'ChatInput', element: 'agentToggle', key: selectedAgentType}}
      />
    </Dropdown>
  );
}

export function AgentToolbar({
  chatSessionId,
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentType,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  realtimeVoiceSessionControls,
}: {
  chatSessionId: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  realtimeVoiceSessionControls: RealtimeVoiceSessionControls;
}): JSX.Element {
  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  return (
    <div
      className={css(styles.agentTypeButtons)}
      onMouseDown={(e) => {
        const textArea = textareaRef.current;
        if (!textArea) {
          return;
        }
        if (document.activeElement === textArea) {
          // Don't steal focus from the text area
          e.preventDefault();
        } else {
          // Focus is if it's not already.
          textArea.focus();
        }
      }}
    >
      <PlusMenu
        fileInputRef={fileInputRef}
        agentType={agentType}
        inputDisabled={inputDisabled}
        publicDataEnabled={publicDataEnabled}
        setPublicDataEnabled={setPublicDataEnabled}
        selectedMcpServerIds={selectedMcpServerIds}
        setSelectedMcpServerIds={setSelectedMcpServerIds}
      />
      {/* Show a spinner while settings are loading (only possible for existing chats) */}
      {agentType == null ? (
        <Spinner aria-label="Loading agent settings" />
      ) : (
        (() => {
          const availableAgentTypes = getAvailableAgentTypes(allowedChatTypes);
          const defaultAgentType = getDefaultAgentType(null, allowedChatTypes);
          const isNonDefaultAgent = agentType !== defaultAgentType;
          const unifiedAgentsInOrder = availableAgentTypes.filter((a) =>
            isAssistantUnifiedToolbarAgentType(a)
          );
          const showUnifiedAssistantDropdown =
            BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
            unifiedAgentsInOrder.length >= 2 &&
            (!isNonDefaultAgent || isAssistantUnifiedToolbarAgentType(agentType));

          let unifiedAssistantDropdownRendered = false;

          return availableAgentTypes.map((option: AgentType) => {
            if (showUnifiedAssistantDropdown && isAssistantUnifiedToolbarAgentType(option)) {
              if (unifiedAssistantDropdownRendered) {
                return null;
              }
              unifiedAssistantDropdownRendered = true;
              const dropdown = (
                <UnifiedAssistantAgentToolbarDropdown
                  key="unified-assistant-agents"
                  disabled={inputDisabled}
                  selectedAgentType={agentType}
                  unifiedAgentsInOrder={unifiedAgentsInOrder}
                  setAgentType={setAgentType}
                />
              );
              const description = getAgentDescriptionForChatInput(agentType);
              if (description == null) {
                return dropdown;
              }
              return (
                <Tooltip key="unified-assistant-agents" label={description} position="bottom">
                  <span className={css(styles.tooltipTriggerWrap)}>{dropdown}</span>
                </Tooltip>
              );
            }

            // If a non-default agent is selected outside the unified group, only that agent is shown,
            // with an X to return to the default. When a unified-group agent (Assistant, Ask, DR,
            // Compose, Analysis) is selected, other agents (e.g. Co-scientist) stay visible and the unified
            // control has no close — switch via the dropdown or another agent's toggle.
            if (
              isNonDefaultAgent &&
              option !== agentType &&
              !(showUnifiedAssistantDropdown && isAssistantUnifiedToolbarAgentType(agentType))
            ) {
              return null;
            }

            const showCloseIcon = isNonDefaultAgent && availableAgentTypes.length > 1 && option === agentType;
            const toggleButton = (
              <ToggleButton
                key={option}
                isActive={option === agentType}
                showCloseIcon={showCloseIcon}
                onClick={() => {
                  if (option === agentType) {
                    if (!showCloseIcon) {
                      return;
                    }
                    setAgentType(defaultAgentType);
                    return;
                  }
                  setAgentType(option);
                }}
                icon={getAgentTypeIcon(option) ?? undefined}
                text={getAgentTypeText(option)}
                disabled={inputDisabled}
                testKey={option}
              />
            );
            const description = getAgentDescriptionForChatInput(option);

            if (description == null) {
              return toggleButton;
            }

            return (
              <Tooltip key={option} label={description} position="bottom">
                <span className={css(styles.tooltipTriggerWrap)}>{toggleButton}</span>
              </Tooltip>
            );
          });
        })()
      )}
      {isLikelyMobileCapabilities() && (
        <Button
          aria-label="Take and upload photo"
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
          size={ButtonSize.MINI}
        />
      )}
      {agentType != null &&
        doesAgentTypeSupportRealtimeVoice(agentType) &&
        chatSessionId != null &&
        (() => {
          switch (realtimeVoiceSessionControls.sessionState) {
            case RealtimeVoiceSessionState.NOT_CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.startVoiceSession}
                  disabled={inputDisabled}
                  icon="sparkles"
                  text="Go live"
                />
              );
            case RealtimeVoiceSessionState.CONNECTING:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  disabled={true}
                  text="Connecting..."
                />
              );
            case RealtimeVoiceSessionState.CONNECTED:
              return (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={realtimeVoiceSessionControls.stopVoiceSession}
                  disabled={inputDisabled}
                  icon="stop"
                  text="Stop"
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  icon: BenchlingIconName | undefined;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {isActive, text, showCloseIcon, onClick, icon, disabled, testKey} = props;
  return (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      icon={icon}
      textClassName={css(styles.agentTypeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
}

const styles = StyleSheet.create({
  tooltipTriggerWrap: {
    display: 'inline-block',
  },
  agentTypeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentTypeButton: {
    fontWeight: 600,
    fontSize: '12px',
    padding: '4px 6px', // reduce horizontal padding, we are in tight space.
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
  },
  agentTypeText: {
    flex: 'none',
  },
  agentMenuOptionWithTag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 12,
    minWidth: 0,
  },
  agentMenuOptionLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  agentMenuOptionTagSlot: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    // Offsets @benchling/nucleus-ui Tag default marginRight (tokens/Style.PAD_2).
    marginRight: '-8px',
  },
});
